<template>
  <div>
    <Plotly :layout="chart.layout" :data="chart.data" :display-mode-bar="false"></Plotly>
    <v-row class="justify-space-around" no-gutters>
      <v-col cols="6"><TableSimple :data="tableValues"></TableSimple></v-col>
      <v-col cols="6">
        <p class="pl-4">{{ settings.text }}</p>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions } from "vuex";
import marketSettings from "@/mixins/marketsSettings";

export default {
  name: "SectionEnergyPrices",
  components: {
    Plotly: () => import("@/components/charts/Plotly.vue"),
    TableSimple: () => import("@/components/visualizations/TableSimple.vue"),
  },
  mixins: [],
  props: {
    caseObj: { type: [Object, null], required: false, default: () => ({}) },
    settings: { type: [Object, null], required: false, default: () => ({}) },
  },
  data() {
    return {
      tableValues: [
        {
          header: { text: "Energy Prices Data", value: "" },
          rows: [],
        },
      ],
      profile: null,
      marketNode: null,
    };
  },
  computed: {
    energyMarket() {
      if (
        this.caseObj &&
        this.caseObj.input.projects.length &&
        this.caseObj.input.projects[0].markets.length
      ) {
        const [market] = this.caseObj.input.projects[0].markets;
        const { energy } = market;
        return energy;
      }
      return null;
    },
    customMarket() {
      if (this.energyMarket && this.energyMarket.source === "Custom") {
        return this.energyMarket;
      }
      return null;
    },
    chartData() {
      if (!this.profile) return {};
      if (this.customMarket) {
        return {
          y: this.profile.value.map((v) => v.value),
          x: this.profile.value.map((v) => v.date),
          marker: { color: this.$vuetify.theme.themes.light.market },
        };
      }
      if (this.energyMarket.source === "Market") {
        const settings = marketSettings[this.energyMarket.market_name];
        return {
          x: this.profile.time_window,
          y: this.profile[settings.priceKey],
        };
      }
      if (this.energyMarket.source === "IHR") {
        return {
          x: this.profile.output.hourly.map((v) => v.timestamp),
          y: this.profile.output.hourly.map((v) => v.energy_price[this.energyMarket.ihr_type]),
        };
      }
      return {};
    },
    chart() {
      let yaxisTitle = "Price";
      if (this.customMarket) {
        yaxisTitle = `Price [${this.customMarket.price_unit}/${this.customMarket.energy_unit}]`;
      } else if (this.energyMarket.source === "Market") {
        yaxisTitle = marketSettings[this.energyMarket.market_name].priceKey;
      }
      return {
        layout: {
          title: { text: "Energy Prices", font: { size: 14 } },
          height: 340,
          yaxis: {
            title: yaxisTitle,
            rangemode: "tozero",
          },
          legend: { orientation: "h" },
        },
        data: this.profile ? [this.chartData] : [],
      };
    },
  },
  watch: {
    caseObj(v) {
      if (v) this.getSectionData();
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (this.caseObj) this.getSectionData();
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      getForecast: "forecasts/get",
      findProfiles: "profiles/find",
      findAction(dispatch, payload, path) {
        return dispatch(`${path}/find`, payload);
      },
    }),
    getSectionData() {
      this.setTableData();
      this.getPriceProfile();
    },
    setTableData() {
      if (this.caseObj.input.projects.length && this.caseObj.input.projects[0].markets.length) {
        const [market] = this.caseObj.input.projects[0].markets;
        const { energy } = market;
        if (energy) {
          if (energy.source === "Custom") {
            // Data from profile
            this.tableValues[0].rows.push(
              ["Prices Source", "Custom"],
              ["Price Unit", energy.price_unit],
              ["Energy Unit", energy.energy_unit]
            );
          }
          if (energy.source === "Market") {
            this.tableValues[0].rows.push(
              ["Prices Source", "Market"],
              ["Market Name", energy.market_name],
              ["Schedule", energy.index_type],
              ["Node", energy.node_id]
            );
            this.getNode(energy.market_name, energy.node_id);
          }
          if (energy.source === "IHR") {
            this.tableValues[0].rows.push(
              ["Prices Source", "IHR"],
              ["IHR ID", energy.node_id],
              ["IHR Type", energy.ihr_type]
            );
          }
        }
      }
    },
    getPriceProfile() {
      if (this.caseObj.input.projects[0].markets.length) {
        const { energy } = this.caseObj.input.projects[0].markets[0];
        if (energy) {
          if (energy.source === "Custom") {
            const params = {
              query: { id: energy.profile[0].id },
            };
            this.findProfiles(params).then((r) => {
              [this.profile] = r;
            });
          } else if (energy.source === "Market") {
            const settings = marketSettings[energy.market_name];
            const keyMap = {
              "Real-Time": "realTimeService",
              "Day-Ahead": "dayAheadService",
            };
            const service = settings[keyMap[energy.index_type]];
            const params = {
              query: {
                [settings.nodeKey]: energy.node_id,
                downsample: "720 minutes",
                $and: [
                  {
                    [settings.dateKey]: {
                      $gt: this.caseObj.settings.timeline_settings.start_date,
                    },
                  },
                  {
                    [settings.dateKey]: {
                      $lt: this.caseObj.settings.timeline_settings.end_date,
                    },
                  },
                ],
                $limit: 750,
              },
            };
            this.findAction(params, service).then((v) => {
              if (v.data.length) {
                [this.profile] = v.data;
              }
            });
          } else if (energy.source === "IHR") {
            this.getForecast(energy.node_id).then((v) => {
              this.profile = v;
              const { input } = v;
              const { settings } = input;
              const IHRMarket = input.energy_ancillary_inputs
                ? input.energy_ancillary_inputs.market_name || ""
                : "";
              const marketNode = IHRMarket ? input.energy_ancillary_inputs.node_id : "";
              if (IHRMarket) {
                this.tableValues[0].rows.push(["Market", IHRMarket]);
              }
              if (marketNode) {
                this.tableValues[0].rows.push(["Market Node", marketNode]);
              }
              const startDate =
                settings && settings.start_of_forecast_date
                  ? DateTime.fromISO(settings.start_of_forecast_date, {
                      zone: "utc",
                    }).toLocaleString(DateTime.DATE_SHORT)
                  : "";
              const endDate =
                settings && settings.end_of_forecast_date
                  ? DateTime.fromISO(settings.end_of_forecast_date, { zone: "utc" }).toLocaleString(
                      DateTime.DATE_SHORT
                    )
                  : "";
              this.tableValues[0].rows.push(["Horizon", `${startDate} - ${endDate}`]);
            });
          }
        }
      }
    },
    getNode(market, id) {
      const settings = marketSettings[market];
      this.findAction(
        {
          query: {
            [settings.nodeKey]: id,
          },
        },
        settings.nodeService
      ).then((r) => {
        if (r.total) {
          [this.marketNode] = r.data;
          this.tableValues[0].rows.splice(
            this.tableValues[0].rows.findIndex((v) => v[0] === "Node"),
            1,
            ["Node", `${this.marketNode[settings.nodeName]}`]
          );
        }
      });
    },
  },
};
</script>

<style></style>
